import React from 'react';
import { Cloud, Server, Database, Lock, Terminal, Activity, Bell } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader.js';
import { Card } from '../../components/Card.js';

export const Architecture: React.FC = () => {
  const pipelineSteps = [
    {
      step: '01',
      title: 'Googleデータの安全な読み取り',
      desc: '書き込みや削除を行わない「読み取り専用」権限で安全にアクセス',
      badge: '読み取り専用',
      badgeColor: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: <Cloud className="w-5 h-5 text-sky-600" />,
    },
    {
      step: '02',
      title: 'パスワード不要の安全連携',
      desc: '漏洩リスクのある秘密鍵ファイルを持たず、Google公式の安全な認証基盤で連携',
      badge: 'キーレス連携',
      badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <Lock className="w-5 h-5 text-emerald-600" />,
    },
    {
      step: '03',
      title: '業務を妨げないスムーズな通信',
      desc: '日中の通常業務に影響が出ないよう、通信負荷を自動調整しながらバックアップ',
      badge: '自動負荷調整',
      badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <Activity className="w-5 h-5 text-amber-600" />,
    },
    {
      step: '04',
      title: '管理者でも消せない金庫へ保存',
      desc: '取得したデータは即座に暗号化され、一定期間削除不可能な隔離ストレージへ保管',
      badge: '消去不可ロック',
      badgeColor: 'bg-teal-50 text-teal-700 border-teal-200',
      icon: <Server className="w-5 h-5 text-teal-600" />,
    },
    {
      step: '05',
      title: '改ざん防止の原本証明ブロック生成',
      desc: 'データに固有の電子指紋を付与し、後からの改ざんを100%検知できる台帳へ記録',
      badge: '改ざん100%検知',
      badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      icon: <Database className="w-5 h-5 text-indigo-600" />,
    },
    {
      step: '06',
      title: 'チャットへ結果をリアルタイム通知',
      desc: 'Google Chatなどの通知先へ、毎日のバックアップ結果や異常を自動報告',
      badge: '即時通知',
      badgeColor: 'bg-rose-50 text-rose-700 border-rose-200',
      icon: <Bell className="w-5 h-5 text-rose-600" />,
    },
  ];

  const specs = [
    { label: 'クラウド基盤', value: 'Google Cloud Platform (GCP)' },
    { label: 'データ保管場所', value: 'Google Workspaceとは別の独立リージョン' },
    { label: '削除不可ロック期間', value: '365日〜無期限（管理者でも削除不能）' },
    { label: '暗号化方式', value: '企業専用の強固な暗号鍵 (AES-256)' },
    { label: '認証方式', value: '漏洩リスクゼロのキーレス認証' },
    { label: '通信制御', value: '自動負荷調整・エラー自動再試行' },
    { label: 'インフラ管理', value: '全構成がコード化され透明性を担保' },
    { label: '復元・検証方式', value: 'いつでも管理画面から検証・復元可能' },
  ];

  return (
    <section id="architecture" className="py-20 md:py-28 relative border-t border-slate-200/80 bg-slate-100/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          tag="データの安全な流れ"
          tagVariant="cyan"
          title="データの取得から隔離保管までの安全な全自動ステップ"
          description="社内サーバーの導入や面倒な保守作業は一切不要。Googleの公式機能と連携し、毎日のデータ取得から暗号化、隔離金庫への保存、結果のチャット通知までをすべて自動で行います。"
        />

        {/* Pipeline Diagram Flow */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pipelineSteps.map((item, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm relative hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-xs font-bold text-slate-400">PHASE {item.step}</span>
                  <span className={`text-[11px] font-mono px-2.5 py-0.5 rounded-full border font-semibold ${item.badgeColor}`}>
                    {item.badge}
                  </span>
                </div>
                <div className="flex items-center gap-3.5 mb-2.5">
                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    {item.icon}
                  </div>
                  <h4 className="text-base font-bold text-slate-900 tracking-tight">{item.title}</h4>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Specification Table */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white border-slate-200 p-6 sm:p-10 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2.5">
              <Terminal className="w-5 h-5 text-emerald-600" />
              システム仕様・セキュリティ基準
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-xs">
              {specs.map((s, i) => (
                <div key={i} className="flex justify-between py-2.5 border-b border-slate-100">
                  <span className="text-slate-500 font-medium">{s.label}</span>
                  <span className="text-slate-800 font-mono font-bold text-right">{s.value}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
